import React, { useEffect, useState } from 'react'
import { Box, TextField, Button, Typography, Alert, Divider, FormControlLabel, Checkbox } from '@mui/material'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { useLazyGetAccountActiveQuery, useLazyGetAccountStatusQuery } from '../../api/account/getAccountStatus'
import { get } from '../../utils/lodash'
import AnonymousLoginForm from './AnonymousLoginForm'

const AnonymousAccess = ({ onBack }) => {
  const [formData, setFormData] = useState({
    accountNumber: '',
    invoiceNumber: 0,
    email: '',
    wantsUpdates: false,
  })
  const [error, setError] = useState('')
  const [getAccountActive, { data: currentAccount, isSuccess: isGetAccountActiveSuccess }] = useLazyGetAccountActiveQuery()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isActiveAccount, setIsActiveAccount] = useState(false)
  const [showAnonForm, setShowAnonForm] = useState(false)

  const handleSubmit = async e => {
    e.preventDefault()

    if (!formData.accountNumber || !formData.invoiceNumber) {
      setError('Account Number and Invoice Number are required')
      return
    }

    // Email validation if provided
    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      getAccountActive({ ...formData })
    } catch (err) {
      setError('Unable to verify information. Please try again.')
    }
  }

  useEffect(() => {
    if (isGetAccountActiveSuccess) {
      const businessName = get(currentAccount, 'businessName', '')
      const accountId = get(currentAccount, 'accountId', '')

      setSearchParams({ business_name: businessName, account_id: accountId })
      setIsActiveAccount(true)
    }
  }, [isGetAccountActiveSuccess])

  useEffect(() => {
    if (isActiveAccount) {
      setShowAnonForm(true)
    }
  }, [isActiveAccount])

  const handleInputChange = field => e => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'wantsUpdates' ? e.target.checked : e.target.value,
    }))
    setError('') // Clear error when user starts typing
  }

  return (
    <>
      {!showAnonForm ? (
        <Box sx={{ p: 3, width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 1 }} align="center">
            One-Time Access
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }} align="center">
            Enter your account details to access your information
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              label="Account Number"
              variant="outlined"
              margin="normal"
              value={formData.accountNumber}
              onChange={handleInputChange('accountNumber')}
              placeholder="Enter your account number"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              required
              fullWidth
              label="Invoice Number"
              variant="outlined"
              margin="normal"
              value={formData.invoiceNumber}
              onChange={handleInputChange('invoiceNumber')}
              placeholder="Enter your invoice number"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              fullWidth
              label="Email Address (Optional)"
              variant="outlined"
              margin="normal"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              placeholder="Enter your email address"
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Provide your email to receive a copy of your information"
            />

            {formData.email && (
              <FormControlLabel
                control={<Checkbox checked={formData.wantsUpdates} onChange={handleInputChange('wantsUpdates')} color="primary" />}
                label="Keep me updated about my account status"
                sx={{ mt: 1 }}
              />
            )}

            <Box
              sx={{
                mt: 3,
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button variant="outlined" fullWidth onClick={onBack} sx={{ order: { xs: 2, sm: 1 } }}>
                Back to Login
              </Button>
              <Button type="submit" variant="contained" color="primary" fullWidth sx={{ order: { xs: 1, sm: 2 } }}>
                Access Account
              </Button>
            </Box>
          </form>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Typography variant="h6" sx={{ mb: 1 }} align="center">
              Step 2: Create your login
            </Typography>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }} align="center">
              Great, we found your account! You can access your account below
            </Typography>

            <Divider sx={{ mb: 3 }} />
          </Box>
          <AnonymousLoginForm key="anonymous-login" onBack={() => onBack('anonymous')} />
        </Box>
      )}
    </>
  )
}

export default AnonymousAccess
